import React from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { AlertCircle, Link2 } from "lucide-react";

interface AuthLayoutProps {
  children: React.ReactNode;
  title: string;
  description: string;
  error?: string | null;
  showBranding?: boolean;
}

export function AuthLayout({
  children,
  title,
  description,
  error,
  showBranding = true,
}: AuthLayoutProps) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Branding Header */}
        {showBranding && (
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <div className="flex items-center space-x-2">
                <div className="p-2 bg-blue-600 rounded-lg">
                  <Link2 className="h-6 w-6 text-white" />
                </div>
                <h1 className="text-3xl font-bold text-gray-900">go2url.me</h1>
              </div>
            </div>
            <p className="text-gray-600 max-w-sm mx-auto">
              Create short, powerful links with advanced QR codes and analytics
            </p>
          </div>
        )}

        {/* Main Auth Card */}
        <Card className="shadow-lg border-0 bg-white/95 backdrop-blur-sm">
          <CardHeader className="text-center pb-4">
            <CardTitle className="text-xl font-semibold text-gray-900">
              {title}
            </CardTitle>
            <CardDescription className="text-gray-600">
              {description}
            </CardDescription>
          </CardHeader>

          <CardContent className="pt-0">
            {/* Global Error Message */}
            {error && (
              <div
                className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4"
                role="alert"
                aria-live="polite"
                aria-describedby="error-message"
              >
                <div className="flex items-start">
                  <AlertCircle
                    className="h-4 w-4 text-red-600 mt-0.5 mr-2 flex-shrink-0"
                    data-testid="error-icon"
                  />
                  <div className="flex-1">
                    <p id="error-message" className="text-sm text-red-800">
                      {error}
                    </p>
                    {error.includes("verify your email") && (
                      <p className="text-sm text-red-700 mt-2">
                        <a
                          href="/verify-email"
                          className="underline hover:text-red-900"
                        >
                          Click here to resend verification email
                        </a>
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {children}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-sm text-gray-600">
          <p>
            By continuing, you agree to our{" "}
            <a
              href="https://www.go2url.me/terms"
              className="text-blue-600 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded"
            >
              Terms of Service
            </a>{" "}
            and{" "}
            <a
              href="https://www.go2url.me/privacy"
              className="text-blue-600 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded"
            >
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

